import { useEffect } from "react";

export interface DocumentMeta {
  title: string;
  description: string;
  canonical: string;
  ogTitle?: string;
  ogDescription?: string;
  ogUrl?: string;
  ogImage?: string;
}

/** Find or create a meta tag, set its content, and return a restore function. */
function upsertMeta(attr: "name" | "property", key: string, content: string): () => void {
  const selector = `meta[${attr}="${key}"]`;
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  const previous = el ? el.content : null;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.content = content;
  return () => {
    if (previous != null) el.content = previous;
    else el.remove();
  };
}

/** Find or create the canonical link tag, and return a restore function. */
function upsertCanonical(href: string): () => void {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  const previous = el ? el.href : null;
  if (!el) {
    el = document.createElement("link");
    el.rel = "canonical";
    document.head.appendChild(el);
  }
  el.href = href;
  return () => {
    if (previous != null) el.href = previous;
    else el.remove();
  };
}

/**
 * Per-route document head: sets title, description, canonical and Open Graph
 * tags on mount, restoring whatever was there before on unmount.
 */
export function useDocumentMeta(meta: DocumentMeta): void {
  const { title, description, canonical, ogTitle, ogDescription, ogUrl, ogImage } = meta;
  useEffect(() => {
    const previousTitle = document.title;
    document.title = title;
    const restores: Array<() => void> = [
      upsertMeta("name", "description", description),
      upsertCanonical(canonical),
    ];
    if (ogTitle) restores.push(upsertMeta("property", "og:title", ogTitle));
    if (ogDescription) restores.push(upsertMeta("property", "og:description", ogDescription));
    if (ogUrl) restores.push(upsertMeta("property", "og:url", ogUrl));
    if (ogImage) restores.push(upsertMeta("property", "og:image", ogImage));
    return () => {
      document.title = previousTitle;
      for (const restore of restores) restore();
    };
  }, [title, description, canonical, ogTitle, ogDescription, ogUrl, ogImage]);
}

/**
 * Injects an application/ld+json script tag on mount, removes it on unmount.
 * `data` should be a stable (module-level) object. `<` is escaped so a stray
 * `</script>` inside a string can never break out of the tag.
 */
export function useJsonLd(id: string, data: object): void {
  useEffect(() => {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.dataset.nokoLd = id;
    script.text = JSON.stringify(data).replace(/</g, "\\u003c");
    document.head.appendChild(script);
    return () => script.remove();
  }, [id, data]);
}
